import React, {useEffect, useMemo, useRef, useState} from "react";
import {Row, Col} from "reactstrap";
import BarLoader from "react-spinners/BarLoader";
import _ from "../Trans";
import {
    faChartSimple,
    faSliders,
    faChevronDown,
    faChevronUp,
    faMagnifyingGlass,
    faSuitcaseMedical,
    faCircle,
    faPlus, faMoneyBillTrendUp
} from '@fortawesome/free-solid-svg-icons';
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import 'bootstrap/dist/css/bootstrap.css';
import { toast } from 'react-toastify';
import token from '../AuthToken';
import PlayerInfoModal from './StatsModal';
import BidModal from './BidModal';
import Config from '../Config';
import Currency from './Currency'
import Valuation from "./Valuation";
import Paginator from "./Paginator";
import ReactGA from "react-ga4";
import addDefaultSrc from "./ImageErrorHandler";

function getApiUrlWithSingleParamsList(apiUrl) {
    // take params list, remove param duplicates and form url, so we do not cache
    // things like ?name=&price_min and ?name=&name=&price_min twice
    let [url, tempParams] = apiUrl.split("?");
    let params = tempParams.split('&').reduce((b, a) => {
        let t = a.split('=');
        if (t[0] === "" || t[0] === undefined) {
            return b;
        } else b[t[0]] = t[1];
        return b;
    }, {});
    apiUrl = new URL(url);
    Object.keys(params).forEach(k => {
        if (k === "" || k === "undefined" || k === undefined) {
            return;
        }
        apiUrl.searchParams.set(k, params[k])
    });
    return apiUrl;
}

export default function PlayerList({updateSquadHandler, squadIds, positionFilterFromSquad, tournament, isDraftMiniLeagueSquad, shouldFetchPlayers}) {
    const MAX_PRICE_FILTER = 900000000;
    let [loading, setLoading] = useState(true);

    const [positionFilter, setPositionFilter] = useState("")
    const [searchField, setNameFilter] = useState("");
    const [minPriceFilter, setMinPriceFilter] = useState(0);
    const [maxPriceFilter, setMaxPriceFilter] = useState(MAX_PRICE_FILTER);
    const [teamFilter, setTeamFilter] = useState("0");
    const [players, setPlayerData] = useState([]);
    const [countPlayers, setCountPlayers] = useState(0);
    const [orderByField, setOrderByField] = useState("id");
    const [next, setNext] = useState(null)
    const [last, setLast] = useState(null)
    const [count, setCount] = useState(0)
    const [targetLink, setTargetLink] = useState(null);
    const [minHeight, setMinHeight] = useState("10vh");
    const [height, setHeight] = useState("10vh");
    const searchButtonRef = useRef();
    const lastFetchedUrlRef = useRef(null);
    const [toggle, setToggle] = useState(false);
    const [operationInProgress, setOperationInProgress] = useState(false);

    const getApiUrl = useMemo(() => {
        let apiUrl = targetLink;
        if(targetLink === null) {
            apiUrl = Config.playerListUrl;
        }
        if(positionFilter !== null && parseInt(positionFilter) !== 0) {
            apiUrl += "&position="+positionFilter;
        }
        if(teamFilter !== null && parseInt(teamFilter) !== 0) {
            apiUrl += "&team="+teamFilter;
        }
        if(searchField !== null && searchField !== "") {
            apiUrl += "&name="+searchField;
        }
        apiUrl += "&price_min="+minPriceFilter+"&price_max="+maxPriceFilter;
        apiUrl += "&ordering="+orderByField;
        apiUrl += "&tournament="+tournament;
        if(isDraftMiniLeagueSquad) {
            apiUrl += "&draft=1";
        }

        apiUrl = getApiUrlWithSingleParamsList(apiUrl);
        return apiUrl.toString();
    }, [targetLink, positionFilter, teamFilter, searchField, minPriceFilter, maxPriceFilter, orderByField, tournament, isDraftMiniLeagueSquad]);

    const fetchPlayers = async() => {
        if (tournament == null) {
            return;
        }
        if(lastFetchedUrlRef.current === getApiUrl) {
            return;
        }
        lastFetchedUrlRef.current = getApiUrl;
        setLoading(true);
        await fetch(
                getApiUrl, {
                headers: {
                    Authorization: 'Token '+token,
                    'Content-Type': 'application/json'
                }
            })
            .then((res) => res.json())
            .then((d) => {
                setPlayerData(d.results)
                setCountPlayers(d.count)
                setNext(d.next)
                setLast(d.previous)
                setCount(d.count)
                setTargetLink(null);
            })
            .then(() => setLoading(false)).catch(() => {
                setLoading(false);
                lastFetchedUrlRef.current = null;
            })
        try {
            ReactGA.event({
                action: "search",
                category: 'search',
                label: 'submit'
            })
        } catch(e) {
            console.error(e);
            //todo: window.location.href = window.location;
        }
    }

    useEffect(() => {
        if(shouldFetchPlayers) {
            lastFetchedUrlRef.current = null;
            fetchPlayers();
        }
    }, [shouldFetchPlayers]);  // eslint-disable-line

    const toPageHandler = function(link) {
        setTargetLink(link);
    }

    useEffect(() => {
        if(targetLink !== null) {
            fetchPlayers();
        }
    }, [targetLink]); // eslint-disable-line

    useEffect(() => {
        if(tournament != null) {
            fetchPlayers();
        }
    }, [tournament]); // eslint-disable-line

    useEffect(() => {
        if(positionFilterFromSquad === 0 || positionFilterFromSquad === null) {
            return;
        }
        setToggle(true);
        if(positionFilterFromSquad !== 0) {
            setPositionFilter(positionFilterFromSquad);
        }
    }, [positionFilterFromSquad]);

    useEffect(() => {
        searchButtonRef.current?.click();
    }, [positionFilter]);

    const handlePlayerPositionSelectorChange = e => {
        setPositionFilter(e.target.value);
        try {
            ReactGA.event({
                action: "search",
                category: 'position',
                label: e.target.options[e.target.options.selectedIndex].text
            })
        } catch(e) {
            console.error(e);
        }
    }

    const handlePlayerTeamSelectorChange = e => {
        setTeamFilter(e.target.value);
        try {
            ReactGA.event({
                action: "search",
                category: 'team',
                label: e.target.options[e.target.options.selectedIndex].text
            })
        } catch(e) {
            console.error(e);
        }

    }

    const handlePlayerNameFilter = e => {
        setNameFilter(e.target.value);
        try {
            ReactGA.event({
                action: "search",
                category: "name",
                label: e.target.value
            })
        } catch(e) {
            console.error(e);
        }
    }

    const handleMinimumPriceFilter = e => {
        setMinPriceFilter(e.target.value);
        try {
            ReactGA.event({
                action: "search",
                category: "min price",
                label: e.target.value
            })
        } catch(e) {
            console.error(e);
        }
    }

    const handleMaximumPriceFilter = e => {
        setMaxPriceFilter(e.target.value);
        try {
            ReactGA.event({
                action: "search",
                category: 'max price',
                label: e.target.value
            })
        } catch(e) {
            console.error(e);
        }
    }

    useEffect(() => {
        fetchPlayers()
    }, []);  // eslint-disable-line

    const nameComparisonCollator = new Intl.Collator('es')

    const sortByNameComparison = function(a, b, checkReverse=true) {
        const bigger = nameComparisonCollator.compare(a['name'], b['name']);
        if(bigger === 0) {
            return bigger;
        }
        if (orderByField[0] === "-" && checkReverse) {
            return bigger === 1 ? -1 : 1;
        }
        return bigger === 1 ? 1 : -1;
    }

    const sortByPriceComparison = function(a, b) {
        if(a['price'] === b['price']) {
            return 0;
        }
        const bigger = a['price'] > b['price'];
        if (orderByField[0] === "-") {
            return bigger ? 1 : -1;
        } else {
            return bigger ? -1 : 1;
        }
    }

    const sortByAverageComparision = function(a, b) {
        if(a['valuation_average'] === b['valuation_average']) {
            return 0;
        }
        const bigger = a['valuation_average'] > b['valuation_average'];
        if (orderByField[0] === "-") {
            return bigger ? 1 : -1;
        } else {
            return bigger ? -1 : 1;
        }
    }

    const orderPlayers = function() {
        if(countPlayers === 0) {
            return
        }
        let sortedPlayers = [...players];
        sortedPlayers = sortedPlayers.sort((a, b) => {
            if (orderByField.slice(-4) === 'name') {
                return sortByNameComparison(a, b);
            } else if (orderByField.slice(-5) === 'price') {
                const result = sortByPriceComparison(a, b);
                return result === 0 ? sortByNameComparison(a, b, false) : result;
            } else if (orderByField.slice(-7) === 'average') {
                const result = sortByAverageComparision(a, b);
                return result === 0 ? sortByNameComparison(a, b, false) : result;
            }
            return 0;
        });
        setPlayerData(sortedPlayers);
    }

    useEffect(() => {
        if(countPlayers > process.env.REACT_APP_PAGE_LIMIT) {
            fetchPlayers()
        }
        else {
            orderPlayers();
        }
    }, [orderByField]);  // eslint-disable-line

    const orderBy = (e) => {
        let orderByValue = e.currentTarget.dataset.orderBy
        let newValue = null;
        if(orderByValue === orderByField) {
            newValue = orderByField[0] === "-" ? orderByField.slice(1) : "-" + orderByField;
        }
        else {
            newValue = orderByValue;
        }
        e.currentTarget.setAttribute("data-order", newValue)
        if (e.currentTarget.id === 'market_price') {
            setPriceChevronDown(!priceChevronDown)
        } else if(e.currentTarget.id === '_valuation_average') {
            setAverageChevronDown(!averageChevronDown)
        } else {
            setNameChevronDown(!nameChevronDown)
        }
        setOrderByField(newValue)
    }

    let paddingZero =  {'paddingLeft': 0, 'paddingRight': 0, 'marginBottom': '250px'}
    let selectorStyle = {'color': 'grey'}
    let filterAndSortingTitleStyles = {'paddingTop': "5px", 'marginLeft': "10px"}
    let orderStyle = {
        'marginLeft': "5px"
    }
    let chevronStyle = {
        'marginRight': "15px",
        'paddingTop': '7px'
    }

    const [showStatsModal, setShowStatsModal] = useState(false);
    const [showBidModal, setShowBidModal] = useState(false);
    const [modalData, setModalData] = useState(null);
    const [nameChevronDown, setNameChevronDown] = useState(false);
    const [priceChevronDown, setPriceChevronDown] = useState(false);
    const [averageChevronDown, setAverageChevronDown] = useState(false);
    const showHandler = (e) => {
        setModalData(e.currentTarget.dataset);
        const target = e.currentTarget.dataset.target;

        if (target === '#player-stats-modal') {
            setShowStatsModal(true);
            ReactGA.event({
                action: "show_stats",
                category: 'stats',
                label: 'player_list'
            })
        } else if (target === '#bid-modal') {
            setShowBidModal(true);
        }
    }

    const toggleSearch = function() {
        setToggle(!toggle);
    }

    useEffect(() => {
        setMinHeight(toggle ? "24vh":"10vh")
        setHeight(toggle ? "24vh": "5vh")
    }, [toggle]);

    const hideHandler = (e) => {
        if(showStatsModal)
            setShowStatsModal(false);
        if (showBidModal)
            setShowBidModal(false);
    }
    const [teamsListForSelect, setTeamsListForSelect] = useState([])
    const fetchTeams = async() => {
        if(tournament == null) {
            tournament = 1;
        }
        let url = Config.tournamentTeamsUrl.replace('/0/', '/'+tournament+'/');
        await fetch(
            url, {
            headers: {
                Authorization: 'Token '+token,
                'Content-Type': 'application/json'
            }
        })
        .then((res) => res.json())
        .then((res) => res.results
            .filter(el => el.selectable)
            .map((el) => {
                return {...el, name: _(el.name) || el.name}
            })
        )
        .then((res) => {
            return Object.entries(res).map(
                ([id, value]) =>  <option key={value.id} id={value.id} value={value.id}>{value.name}</option>
            );
        })
        .then((res) => {
            setTeamsListForSelect(res);
        })
    }

    useEffect(() => {
        (async function fetchTeamsInEffect() {
            await fetchTeams();
        })();
    }, [tournament]) // eslint-disable-line

    const handlePressKey = function (e) {
        if(e.key === "Enter") {
            fetchPlayers();
        }
    }

    const toastConfig = {
        position: "top-right",
        autoClose: 2000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
    }

    const orderBarBottomHeight = "200px";

    const bidForPlayer = e => {
        let data = {
            player_id: parseInt(e.currentTarget.dataset.player),
            amount: parseInt(document.getElementById('id_bid').value)
        }
        if(operationInProgress) {
            return;
        }
        setOperationInProgress(true);
        fetch(Config.bidForPlayerUrl, {
            method: 'POST',
            headers: {
                Authorization: 'Token '+token,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(data)
        })
        .then((e) => e.json())
        .then((e, data) => {
            if(!e.detail) {
                toast.success(_("You are the current high bidder"), toastConfig)
                updateSquadHandler();
            }
            else {
                try {
                    let message = Config.errorMessages[e?.detail];
                    if(message === undefined) {
                        console.error(e.detail);
                        message = _('Unable to bid for player');
                    }
                    toast.error(message, toastConfig)
                }
                catch(error) {
                    toast.error(_(error), toastConfig)
                }
            }
        }).catch(
            (e) => { console.error("Bidding for player", e); }
        )
        .finally(() => setOperationInProgress(false));
    }

    const addPlayerToSquad = e => {
        let data = {
            player: e.currentTarget.dataset.player,
            position: e.currentTarget.dataset.position
        }
        if(operationInProgress) {
            return;
        }
        setOperationInProgress(true);
        fetch(Config.addPlayerToSquadUrl, {
            method: 'POST',
            headers: {
                Authorization: 'Token '+token,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(data)
        })
        .then((e) => e.json())
        .then((e, data) => {
            if(!e.detail) {
                updateSquadHandler(e)
                toast.success(_("Player purchased"), toastConfig)
            }
            else {
                try {
                    let message = Config.errorMessages[e?.detail];
                    if(message === undefined) {
                        console.error(e.detail);
                        message = _('Unable to buy player');
                    }
                    toast.error(message, toastConfig)
                }
                catch(error) {
                    toast.error(_(error), toastConfig)
                }
            }
        }).catch(
            (e) => { console.error("Error adding player to squad", e); }
        )
        .finally(() => setOperationInProgress(false));
    }

    return <Col xs={12} md={6} className="mt-5 mt-md-0 p-0 court-container" style={paddingZero} id="playerList">
        <PlayerInfoModal showValue={showStatsModal} modalData={modalData} showValueToggleHandler={hideHandler} addPlayerToSquadHandler={addPlayerToSquad}/>
        {(isDraftMiniLeagueSquad) && <BidModal showBidModal={showBidModal} modalData={modalData} showValueToggleHandler={hideHandler} bidForPlayer={bidForPlayer}/>}
        <div className="d-flex justify-content-between mt-3 mt-md-0 w-100 filter-bar" onClick={toggleSearch} id="filterTab">
            <div className="h5" style={filterAndSortingTitleStyles}>
                <FontAwesomeIcon icon={faSliders} className="mr-1 pr-1"/>&nbsp;&nbsp;&nbsp;{_('Filter and sorting')}
            </div>
            <div className="float-right"><FontAwesomeIcon icon={toggle ? faChevronUp : faChevronDown}
                                                          style={chevronStyle}/></div>
        </div>
        {toggle && (
            <div>
                <div className="d-flex d-flex-column col-12 pl-0 pr-0">
                    <select id="filterPlayers" onChange={handlePlayerPositionSelectorChange}
                            className="form-control w-50 b-0 mt-0"
                            style={{'paddingBottom': '0px', 'paddingTop': '0px', 'paddingLeft': '5px'}}
                            value={positionFilter}
                    >
                        <option id="" value="0" style={selectorStyle}>{_('Position')}</option>
                        <option id="" value="1">{_('Goalkeeper')}</option>
                        <option id="" value="2">{_('Center back')}</option>
                        <option id="" value="3">{_('Right back')}</option>
                        <option id="" value="4">{_('Left back')}</option>
                        <option id="" value="5">{_('Pivot')}</option>
                        <option id="" value="6">{_('Right wing')}</option>
                        <option id="" value="8">{_('Left wing')}</option>
                    </select>
                    <input
                        className="form-control w-50 w-md-100 b-0 pt-0 pb-0 pl-2 pr-2 mt-0"
                        type="search"
                        placeholder={_("Name")}
                        onChange={handlePlayerNameFilter}
                        onKeyDown={handlePressKey}
                    />
                </div>
                <div className="d-flex d-flex-column col-12 pl-0 pr-0">
                    <input
                        className="form-control w-50 w-md-100 b-0 pt-0 pb-0 pl-2 pr-2 mt-0"
                        type="search"
                        placeholder={_("Minimum price")}
                        onChange={handleMinimumPriceFilter}
                        onKeyDown={handlePressKey}
                    />
                    <input
                        className="form-control w-50 w-md-100 b-0 pt-0 pb-0 pl-2 pr-2 mt-0"
                        type="search"
                        placeholder={_("Maximum price")}
                        onChange={handleMaximumPriceFilter}
                        onKeyDown={handlePressKey}
                    />
                </div>
                <div className="d-flex d-flex-column col-12 pl-0 pr-0">
                    <select id="filterByTeam" onChange={handlePlayerTeamSelectorChange}
                            className="form-control w-50 b-0 mt-0"
                            style={{'paddingBottom': '0px', 'paddingTop': '0px', 'paddingLeft': '5px'}}
                            value={teamFilter}
                    >
                        <option id="0" value="0" style={selectorStyle}>{_('Team')}</option>
                        {teamsListForSelect}
                    </select>
                    <button ref={searchButtonRef} onClick={fetchPlayers} className="btn btn-primary right" style={{'width': '50%'}} color="primary">{_('Filter')}&nbsp;&nbsp;<FontAwesomeIcon icon={faMagnifyingGlass}/></button>
                </div>
            </div>
        )}
        <div className="order-bar">
            <div className="w-100 mt-2 d-inline-block" style={orderStyle}>
                <div className="mr-4 float-left">
                    {_('Order by')}:
                </div>
                <div id="name" onClick={orderBy} data-order-by="name" className="link mr-4 float-left">
                    {_('Name')} {orderByField === "name" || orderByField === "-name" ? <FontAwesomeIcon icon={nameChevronDown ? faChevronDown : faChevronUp}/> : ""}
                </div>
                <div id="market_price" onClick={orderBy} data-order-by="market_price" className="link mr-4 float-left">
                    {_('Price')} {orderByField === "market_price" || orderByField === "-market_price" ? <FontAwesomeIcon icon={priceChevronDown ? faChevronUp : faChevronDown}/> : ""}
                </div>
                <div id="_valuation_average" onClick={orderBy} data-order-by="_valuation_average" className="link mr-4 float-left">
                    {_('Average')} {orderByField === "_valuation_average" || orderByField === "-_valuation_average" ? <FontAwesomeIcon icon={averageChevronDown ? faChevronUp : faChevronDown}/> : ""}
                </div>
                <div className="float-right mr-4">
                <Paginator next={next} previous={last} count={count} toPageHandler={toPageHandler}/>
                </div>
            </div>
        </div>
        <div className="players players-list">
            {loading
                ? <div className="text-center w-100 d-flex justify-content-center mt-5"><BarLoader
                        color="#23a6da"
                        loading={loading}
                        width={400}
                        aria-label="Loading Spinner"
                        data-testid="loader"
                /></div>
                :
                <div>
                    <SearchList
                        filteredPlayers={players}
                        updateSquadHandler={updateSquadHandler}
                        squadIds={squadIds}
                        show={showHandler}
                        addPlayerToSquadHandler={addPlayerToSquad}
                        isDraftMiniLeagueSquad={isDraftMiniLeagueSquad}
                    />
                    <div style={{"minHeight": minHeight, "height": height}}>&nbsp;</div>
                </div>
            }
            <div className="order-bar" style={{height: "50px"}}>
                <div className="w-100 mt-2 d-inline-block" style={orderStyle}>
                    <div className="float-right mr-4">
                    <Paginator next={next} previous={last} count={count} toPageHandler={toPageHandler}/>
                    </div>
                </div>
            </div>
            <div style={{height: orderBarBottomHeight}}></div>
        </div>
    </Col>
}

function SearchList({filteredPlayers, updateSquadHandler, squadIds, show, addPlayerToSquadHandler, isDraftMiniLeagueSquad}) {
    if(filteredPlayers === undefined) {
        return "";
    }

  const playersToDisplay = filteredPlayers.map(
      player => <Player key={player.id} player={player} updateSquadHandler={updateSquadHandler} grayed={squadIds.includes(player.id) || !player.team_selectable || player.injured} show={show} addPlayerToSquadHandler={addPlayerToSquadHandler} isDraftMiniLeagueSquad={isDraftMiniLeagueSquad}/>
  );

  return (
    <div className="player">
      {playersToDisplay.length !== 0
        ? playersToDisplay
        : <NoPlayersFoundElement/>
      }
    </div>
  );
}

function Player({player, updateSquadHandler, grayed, show, addPlayerToSquadHandler, isDraftMiniLeagueSquad}) {
    let playerClass = "image image_"+player.id;
    const displayInlineBlock = {"display" : "inline-block"}
    const last3Valuations = player.last_3_valuations.join(",")
    const flag = getPlayerFlag(player?.team_flag);
    const injuredText = _('Injured');

    let grayedClasses = "w-100 justify-content-between player-list-player";
    if(grayed) {
        grayedClasses += " already-selected";
    }
    const injuredIconStyle = { color: 'white', 'position': 'absolute', 'top': '-1px', 'left': '5px'/*, transform: 'rotate(45deg)'*/};
    const injuredIconStyleBack = { color: 'red', 'position': 'absolute', 'left': '5px', height:'11px'};
    const average = player.valuation_average !== null ? Math.ceil(player.valuation_average) : null;
    const parseValuations = function(player) {
        try {
            let value = player.valuations.reduce((value, obj) => {value.push(obj.value); return value}, []);
            return value.join(",");
        }
        catch(e) {
            console.error("Parsing valuations", e);
            return [];
        }
    }

    const teamName = _(player.team_name.replace(" (F)", ""))

    return (
        <Row className={grayedClasses}>
            <Col xs={3} sm={3} md={3} lg={3} xl={2} className="pt-4 pt-lg-3 pl-0 player-list-image" style={displayInlineBlock}>
                <img className={playerClass}
                     data-position={player.position_id}
                     data-price={player.price}
                     data-team={player.team_id}
                     src={player.image_link}
                     loading="lazy"
                     alt={player.name}
                     title={player.name}
                     onError={addDefaultSrc}
                     // style={{'maxHeight': "100px", "overflow": "hidden", "maxWidth": "max-content"}}
                    style={{"overflow": "hidden"}}
                />
            </Col>
            <Col xs={5} sm={4} md={4} lg={4} xl={4} className="pt-4 pt-lg-3 pl-2 pl-md-0 pr-0 d-table-row player-list-data">
                <div className="h6 font-weight-bold mb-0 float-left text-left w-100">
                    {player.name} {player.injured &&
                      <div className="fa-stack fa-2x" style={{ height: 'auto', width: 'auto'}}>
                        <FontAwesomeIcon icon={faCircle} className="fa-solid fa-stack-1x" style={injuredIconStyleBack} alt={injuredText} size={"xs"}/>
                        <FontAwesomeIcon icon={faSuitcaseMedical} className="fab fa-stack-1x fa-inverse" style={injuredIconStyle} alt={injuredText} size={"xs"}/>
                      </div>
                    }
                </div>
                <div className="h6 mt-2 pt-0 text-left float-right w-100">
                    <div className="pt-1"><img src={flag} alt={teamName} className="player-list-flag"/> {teamName}</div>
                </div>
                {player?.highest_bidder &&
                    <div className="h6 mt-2 pt-0 text-left float-right w-100">
                    <h6 className='float-left mr-3 d-inline-flex align-items-center' style={{'right': '0'}}>
                        <FontAwesomeIcon icon={faMoneyBillTrendUp} className='mr-2 pb-1'/>
                        <div className='float-right'>{player.highest_bidder}</div>
                    </h6>
                    </div>
                }
            </Col>
            <Col xs={2} sm={3} md={4} lg={4} xl={4} className="pt-4 pt-lg-3 pl-2 pr-md-3 pr-lg-4 d-table-row player-list-data">
                <div className="left float-left">
                    <PositionSpecific position={player?.position_specific}
                                      abbreviation={player?.position_abbreviation}/>
                    <div className="h6 mt-2 mb-0 text-right float-right w-100 d-flex" style={{'direction': 'ltr', justifyContent: 'flex-end'}}>
                        <div className="ml-1 pt-1 pr-2 float-left">{_('Average')}:</div><Valuation valuation={average} isPlayerList={true}/>
                    </div>
                    <div className="h6 mt-2 mb-0 text-right float-right w-100">
                        <div className="mt-1"><Currency value={player.price} extraClass={"pt-2"}/></div>
                    </div>
                </div>
            </Col>
            <Col xs={2} sm={2} md={1} lg={1} xl={1} className="pt-3 pt-lg-3 text-right float-right pl-0 pr-3 offset-md-1 ml-0">
                {isDraftMiniLeagueSquad ?
                    <p className="link add-player-to-squad"
                       data-toggle="modal"
                       data-target="#bid-modal"
                       data-name={ player.name }
                       data-player={ player.id }
                       data-player-id={ player.id }
                       data-info-link=""
                       data-price={player.price}
                       onClick={show}
                    >
                        <FontAwesomeIcon icon={faPlus}/>
                    </p>
                    :
                    <p onClick={addPlayerToSquadHandler} data-player={player.id} data-position={player.position_id} className="link add-player-to-squad">
                        <FontAwesomeIcon icon={faPlus}/>
                    </p>
                }
                <p className="link m-0 pt-3 pt-lg-2"
                   data-toggle="modal"
                   data-target="#player-stats-modal"
                   data-name={ player.name }
                   data-player={ player.id }
                   data-player-id={ player.id }
                   data-position_id={ player.position_id }
                   data-info-link={ player.info_link }
                   data-goals-ratio={ player.goals_ratio }
                   data-stopped-shots-ratio={ player.stopped_shots_ratio }
                   data-valuation-average={ player.valuation_average }
                   data-last-valuations={last3Valuations}
                   data-valuations={parseValuations(player)}
                   data-last-valuation={player.last_valuation}
                   data-price={player.price}
                   data-image-link={player.image_link}
                   onClick={show}
                >
                    <FontAwesomeIcon icon={faChartSimple} className="add-player-to-squad"/>&nbsp;
                </p>
            </Col>
        </Row>
    )
}

function PositionSpecific({position, abbreviation}) {
    return (
        <>
        <div className="h6 mb-0 float-right text-right d-sm-none">{_(abbreviation)}</div>
        <div className="h6 mb-0 float-right text-right d-none d-sm-block">{_(position)}</div>
        </>
    )
}

function NoPlayersFoundElement() {
    return (
        <div className="position-absolute w-100 text-center mt-4 font-color">
                {_("No players found")}
        </div>
    )
}

function getPlayerFlag(flagUrl) {
    try {
        if (flagUrl.includes('http')) {
            return flagUrl;
        }
        return process.env.REACT_APP_STATIC_URL + flagUrl;
    }
    catch(e) {
        return process.env.REACT_APP_STATIC_URL + flagUrl;
    }
}

export {getPlayerFlag}
